/**
 * CenteredCard — narrow centered card on a wide page.
 *
 * Used by auth / single-step forms (SignIn, SignUp, PasswordRecovery) and
 * anywhere the content must be visually anchored to the center of the
 * viewport with a readable column width.
 *
 * maxWidth accepts: xs (320) / sm (400) / md (480) / lg (640).
 * Override with maxWidthPx for an exact value.
 *
 * radius accepts m (12) / l (20) / xl (32). xl is reserved for auth-card
 * chrome (the brand expresses it with the larger, softer corner).
 *
 * padding sets all four sides from a spacing token. Override per axis with
 * paddingX / paddingY, or per side with paddingTop / paddingBottom (top and
 * bottom only — left and right collapse into paddingX). Specificity order:
 * paddingTop / paddingBottom > paddingY > padding for the vertical sides;
 * paddingX > padding for the horizontal sides.
 *
 * contentAlignX picks the cross-axis alignment for the inner Stack
 * (stretch / start / center / end). Default stretch — children fill the
 * card width. Auth cards typically use center to keep hug-content elements
 * (logo, segmented switch) horizontally centered while letting fullWidth
 * inputs / buttons span edge-to-edge.
 *
 * decoration slots in a node rendered absolutely behind the Stack content.
 * When set, the card gets position:relative + overflow:hidden so the
 * decoration is clipped to the rounded corners.
 *
 * cardAs lets the card become a semantic element. Use cardAs=form to hang
 * onSubmit directly on the CenteredCard without an extra wrapper; pass
 * the handler via cardProps.
 *
 * @format
 */

import {forwardRef, type ComponentPropsWithoutRef, type ElementType, type ReactNode} from 'react';

import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';

import {Stack, type StackAlignX} from 'ui/components/layout/Stack';
import {spacingPx, type SpacingTokenName} from 'ui/components/layout/spacing';

export const CENTERED_CARD_MAX_WIDTHS = ['xs', 'sm', 'md', 'lg'] as const;
export type CenteredCardMaxWidth = (typeof CENTERED_CARD_MAX_WIDTHS)[number];

export const CENTERED_CARD_RADII = ['m', 'l', 'xl'] as const;
export type CenteredCardRadius = (typeof CENTERED_CARD_RADII)[number];

const MAX_WIDTH_PX: Record<CenteredCardMaxWidth, number> = {
  xs: 320,
  sm: 400,
  md: 480,
  lg: 640,
};

const RADIUS_PX: Record<CenteredCardRadius, number> = {
  m: 12,
  l: 20,
  xl: 32,
};

export interface CenteredCardProps {
  /** Named max-width preset. Default 'sm' (~400px). */
  maxWidth?: CenteredCardMaxWidth;
  /** Override the max-width with an exact pixel value. Wins over maxWidth. */
  maxWidthPx?: number;
  /** Internal stack gap between children. Default 'm'. */
  gap?: SpacingTokenName;
  /** Card corner radius. Default 'm' (12px). Use 'xl' (32) for auth chrome. */
  radius?: CenteredCardRadius;
  /** Padding token applied to all sides. Default 'l' (24px). */
  padding?: SpacingTokenName;
  /** Horizontal padding override. Wins over padding. */
  paddingX?: SpacingTokenName;
  /** Vertical padding override. Wins over padding for both top and bottom. */
  paddingY?: SpacingTokenName;
  /** Top padding override. Wins over paddingY / padding. */
  paddingTop?: SpacingTokenName;
  /** Bottom padding override. Wins over paddingY / padding. */
  paddingBottom?: SpacingTokenName;
  /** Inner Stack cross-axis alignment. Default 'stretch'. */
  contentAlignX?: StackAlignX;
  /** Render with the card surface. Default true. Disable for chromeless centering. */
  surface?: boolean;
  /** Node rendered absolutely behind content; auto-clipped via overflow:hidden. */
  decoration?: ReactNode;
  /** Element type for the inner card. Default 'div'. Use 'form' for onSubmit. */
  cardAs?: ElementType;
  /** Forwarded onto the inner card element. Useful when cardAs is 'form'. */
  cardProps?: Omit<ComponentPropsWithoutRef<'div'>, 'ref'> & Record<string, unknown>;
  className?: string;
  children?: ReactNode;
}

const Outer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
});

interface CardStyleProps {
  $maxWidthPx: number;
  $surface: boolean;
  $radiusPx: number;
  $paddingXPx: number;
  $paddingTopPx: number;
  $paddingBottomPx: number;
  $hasDecoration: boolean;
}

const Card = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<CardStyleProps>(
  ({
    theme,
    $maxWidthPx,
    $surface,
    $radiusPx,
    $paddingXPx,
    $paddingTopPx,
    $paddingBottomPx,
    $hasDecoration,
  }) => ({
    width: '100%',
    maxWidth: $maxWidthPx,
    boxSizing: 'border-box',
    ...($surface && {
      backgroundColor: theme.palette.surface.surface1,
      borderRadius: $radiusPx,
      paddingTop: $paddingTopPx,
      paddingBottom: $paddingBottomPx,
      paddingLeft: $paddingXPx,
      paddingRight: $paddingXPx,
    }),
    ...($hasDecoration && {
      position: 'relative',
      overflow: 'hidden',
    }),
  }),
);

// Inner stack sits at z-index 1 so any decoration slotted in renders behind it.
const ContentLayer = styled(Box)({
  position: 'relative',
  zIndex: 1,
  width: '100%',
});

export const CenteredCard = forwardRef<HTMLDivElement, CenteredCardProps>(function CenteredCard(
  {
    maxWidth = 'sm',
    maxWidthPx,
    gap = 'm',
    radius = 'm',
    padding = 'l',
    paddingX,
    paddingY,
    paddingTop,
    paddingBottom,
    contentAlignX = 'stretch',
    surface = true,
    decoration,
    cardAs = 'div',
    cardProps,
    className,
    children,
  },
  ref,
) {
  const widthPx = maxWidthPx ?? MAX_WIDTH_PX[maxWidth];
  const radiusPx = RADIUS_PX[radius];
  const paddingXPx = spacingPx(paddingX ?? padding);
  const paddingTopPx = spacingPx(paddingTop ?? paddingY ?? padding);
  const paddingBottomPx = spacingPx(paddingBottom ?? paddingY ?? padding);
  return (
    <Outer className={className}>
      <Card
        ref={ref}
        as={cardAs}
        $maxWidthPx={widthPx}
        $surface={surface}
        $radiusPx={radiusPx}
        $paddingXPx={paddingXPx}
        $paddingTopPx={paddingTopPx}
        $paddingBottomPx={paddingBottomPx}
        $hasDecoration={Boolean(decoration)}
        {...cardProps}
      >
        {decoration}
        <ContentLayer>
          <Stack gap={gap} alignX={contentAlignX}>
            {children}
          </Stack>
        </ContentLayer>
      </Card>
    </Outer>
  );
});
